/**
 * Modal de **alta/edición de sede** (`POST /sedes` / `PATCH /sedes/<id>`).
 *
 * Réplica de `add_sede.html`/`edit_sede.html` del SSR (`progress/
 * explore_admin_ssr.md`): nombre*, ciudad*, dirección, teléfono, email y, solo en
 * edición, el toggle `activa`. Valida con Zod antes de delegar y traduce el error
 * de la API a mensaje legible. Controlado por el padre (`target`). Presentacional
 * + hooks; sin `fetch`.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import { adminErrorMessage } from "../format";
import { useCreateSede, useUpdateSede } from "../hooks/useSedeMutations";
import {
  createSedeSchema,
  editSedeSchema,
  type CreateSedeValues,
  type EditSedeValues,
} from "../schemas";
import type { CreateSedeBody, SedeListItem, UpdateSedeBody } from "../types";

interface SedeFormDialogProps {
  /** `null` mantiene el modal cerrado. En `edit`, la sede a editar. */
  target: { mode: "create" } | { mode: "edit"; sede: SedeListItem } | null;
  onClose: () => void;
}

/** Errores por campo (de Zod). */
type FieldErrors = Partial<
  Record<"nombre" | "ciudad" | "direccion" | "telefono" | "email", string>
>;

interface FormState {
  nombre: string;
  ciudad: string;
  direccion: string;
  telefono: string;
  email: string;
  activa: boolean;
}

const EMPTY_STATE: FormState = {
  nombre: "",
  ciudad: "",
  direccion: "",
  telefono: "",
  email: "",
  activa: true,
};

export function SedeFormDialog({ target, onClose }: SedeFormDialogProps) {
  const createSede = useCreateSede();
  const updateSede = useUpdateSede();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isEdit = target?.mode === "edit";

  // Al abrir/cambiar de objetivo, hidrata el formulario y limpia el estado de
  // mutación. En edición solo hay datos del listado (sin contacto): se piden los
  // de contacto solo si se quieren editar; el PATCH parcial conserva lo omitido.
  useEffect(() => {
    if (target === null) {
      return;
    }
    if (target.mode === "edit") {
      setForm({
        nombre: target.sede.nombre,
        ciudad: target.sede.ciudad ?? "",
        direccion: "",
        telefono: "",
        email: "",
        activa: target.sede.activa,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setFieldErrors({});
    createSede.reset();
    updateSede.reset();
    // Solo nos interesa reaccionar al cambio de objetivo (las mutaciones son
    // estables por render de TanStack Query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function collectErrors(
    fieldErrorsObj: Partial<Record<keyof FormState, string[] | undefined>>,
  ): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fieldErrorsObj) as (keyof FormState)[]).forEach((key) => {
      if (key === "activa") {
        return;
      }
      const message = fieldErrorsObj[key]?.[0];
      if (message) {
        errors[key] = message;
      }
    });
    return errors;
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (target === null) {
      return;
    }

    if (target.mode === "create") {
      const result = createSedeSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitCreate(result.data);
    } else {
      const result = editSedeSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitEdit(target.sede.id, result.data);
    }
  }

  function submitCreate(values: CreateSedeValues) {
    const body: CreateSedeBody = {
      nombre: values.nombre,
      ciudad: values.ciudad,
      direccion: values.direccion,
      telefono: values.telefono,
      email: values.email,
    };
    createSede.mutate(body, { onSuccess: onClose });
  }

  function submitEdit(sedeId: number, values: EditSedeValues) {
    // PATCH parcial: nombre/ciudad/activa siempre (el form los tiene); los datos
    // de contacto solo si se escribieron (vacío = no tocar, el backend conserva).
    const body: UpdateSedeBody = {
      nombre: values.nombre,
      ciudad: values.ciudad,
      activa: values.activa,
    };
    if (values.direccion) {
      body.direccion = values.direccion;
    }
    if (values.telefono) {
      body.telefono = values.telefono;
    }
    if (values.email) {
      body.email = values.email;
    }
    updateSede.mutate({ sedeId, body }, { onSuccess: onClose });
  }

  const activeMutation = isEdit ? updateSede : createSede;
  const apiError = activeMutation.isError
    ? adminErrorMessage(activeMutation.error)
    : null;
  const isSubmitting = activeMutation.isPending;

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogTitle>{isEdit ? "Editar sede" : "Nueva sede"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Modifica los datos de la sede. Deja los datos de contacto en blanco para conservarlos."
            : "Completa los datos para crear una nueva sede."}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="sede-nombre" className="text-sm font-medium text-slate-700">
              Nombre
            </label>
            <Input
              id="sede-nombre"
              autoComplete="off"
              value={form.nombre}
              aria-invalid={fieldErrors.nombre ? true : undefined}
              aria-describedby={fieldErrors.nombre ? "sede-nombre-error" : undefined}
              onChange={(event) => setField("nombre", event.target.value)}
            />
            {fieldErrors.nombre && (
              <p id="sede-nombre-error" className="text-sm text-red-600">
                {fieldErrors.nombre}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="sede-ciudad" className="text-sm font-medium text-slate-700">
              Ciudad
            </label>
            <Input
              id="sede-ciudad"
              autoComplete="off"
              value={form.ciudad}
              aria-invalid={fieldErrors.ciudad ? true : undefined}
              aria-describedby={fieldErrors.ciudad ? "sede-ciudad-error" : undefined}
              onChange={(event) => setField("ciudad", event.target.value)}
            />
            {fieldErrors.ciudad && (
              <p id="sede-ciudad-error" className="text-sm text-red-600">
                {fieldErrors.ciudad}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="sede-direccion" className="text-sm font-medium text-slate-700">
              Dirección{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="sede-direccion"
              autoComplete="off"
              value={form.direccion}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              onChange={(event) => setField("direccion", event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="sede-telefono" className="text-sm font-medium text-slate-700">
              Teléfono{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="sede-telefono"
              autoComplete="off"
              value={form.telefono}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              onChange={(event) => setField("telefono", event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="sede-email" className="text-sm font-medium text-slate-700">
              Email{isEdit ? " (opcional)" : ""}
            </label>
            <Input
              id="sede-email"
              type="email"
              autoComplete="off"
              value={form.email}
              placeholder={isEdit ? "Dejar en blanco para no cambiar" : undefined}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? "sede-email-error" : undefined}
              onChange={(event) => setField("email", event.target.value)}
            />
            {fieldErrors.email && (
              <p id="sede-email-error" className="text-sm text-red-600">
                {fieldErrors.email}
              </p>
            )}
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                checked={form.activa}
                onChange={(event) => setField("activa", event.target.checked)}
              />
              Sede activa
            </label>
          )}

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando…" : isEdit ? "Actualizar" : "Crear sede"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
